import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, RefreshCw, Users } from 'lucide-react';
import { KpiMetricCard } from '../dashboard/KpiMetricCard';
import { MmspClubRegisterModal } from './MmspClubRegisterModal';

const API = axios.create({ baseURL: '/api/v1/mmsp' });
API.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

function apiErrorMessage(err: unknown, fallback: string) {
  const data = (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data;
  return data?.message || data?.error || (err as { message?: string })?.message || fallback;
}

interface ClubRow {
  id: number;
  communityId: number;
  name: string;
  type: string;
  status: string;
  clubCode: string;
  ledgerAccountCode?: string;
  nasasa?: { status?: string; aggregate?: number };
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-amber-50 text-amber-700',
  active: 'bg-emerald-50 text-emerald-700',
  paused: 'bg-blue-50 text-blue-700',
  frozen: 'bg-red-50 text-red-700',
  dissolved: 'bg-gray-100 text-gray-500',
};

export const MmspClubsOverlay: React.FC = () => {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState<ClubRow[]>([]);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await API.get('/ops/clubs');
      setClubs(res.data?.data || []);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Could not load clubs.'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const drafts = clubs.filter((c) => c.status === 'draft').length;
  const active = clubs.filter((c) => c.status === 'active').length;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <KpiMetricCard label="Clubs" metrics={[{ value: String(clubs.length) }]} icon={Users} accent="green" onClick={() => undefined} />
        <KpiMetricCard label="Draft / setup" metrics={[{ value: String(drafts) }]} icon={Users} accent="orange" onClick={() => undefined} />
        <KpiMetricCard label="Active" metrics={[{ value: String(active) }]} icon={Users} accent="blue" onClick={() => undefined} />
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Clubs</h2>
        <div className="flex gap-2">
          <button onClick={load} className="rounded-lg border px-3 py-2 text-sm"><RefreshCw className="inline h-4 w-4" /> Refresh</button>
          <button onClick={() => setOpen(true)} className="rounded-lg bg-[var(--primary)] px-3 py-2 text-sm text-white">
            <Plus className="inline h-4 w-4" /> Register club
          </button>
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="overflow-hidden rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[var(--background)] text-left text-[var(--muted-foreground)]">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Chapters</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">NASASA</th>
            </tr>
          </thead>
          <tbody>
            {clubs.map((club) => (
              <tr key={club.id} className="cursor-pointer border-t hover:bg-slate-50" onClick={() => navigate(`/admin/mmsp/${club.id}`)}>
                <td className="px-4 py-3 font-medium">{club.name}</td>
                <td className="px-4 py-3">{club.type === 'family' ? 'More than one chapter' : club.type}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_COLORS[club.status] || 'bg-gray-100'}`}>{club.status}</span>
                </td>
                <td className="px-4 py-3 font-mono">{club.clubCode}</td>
                <td className="px-4 py-3">{club.nasasa?.status || '—'}</td>
              </tr>
            ))}
            {clubs.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-[var(--muted-foreground)]">No clubs yet. Register a draft to invite a chair.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <MmspClubRegisterModal open={open} onClose={() => setOpen(false)} onCreated={load} />
    </div>
  );
};
